import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {type Sx, Text} from '@mantine/core';
import {useSelector} from '@xstate/react';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';
import {NodeProps} from 'reactflow';

import {EntityMapEntityNodeOrInaccessibleInfoNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {multiLineEllipsisText} from 'styles/Utilities';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import NoAccessChip from 'ui-library/chips/NoAccessChip';
import {BenchlingNodeV2} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNode';
import {BenchlingNodeV2Status} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';

import {EntityMapMachineContext} from './EntityMapMachineProvider';
import EntityNodeHandle from './EntityNodeHandle';

export const HOVER_INFO_TOOLTIP_TEXT = {
  TOO_MANY_RELATIONSHIPS: 'This entity has too many relationships to load',
  ENTITY_MAP_LIMIT_REACHED: 'Can’t load more relationships, entity map limit reached',
};

function EntityNodeHeader(props: EntityMapEntityNodeOrInaccessibleInfoNode): JSX.Element {
  if (isInaccessible(props)) {
    return <NoAccessChip size={ChipSize.SMALL} />;
  }
  return (
    <>
      <BioentityChip
        id={props.id}
        size={ChipSize.SMALL}
        className={css(ENTITY_NODE_HEADER_CHIP_STYLES.chip)}
      />
      {props.schemaName ? (
        <Text
          c="gray.6"
          fz="sm"
          sx={{
            ...(multiLineEllipsisText(2) as Sx),
          }}
        >
          {props.schemaName}
        </Text>
      ) : null}
    </>
  );
}

/**
 * Renders a node within the entity map for the given entity.
 */
export default function EntityNode(props: NodeProps<EntityMapEntityNodeOrInaccessibleInfoNode>): JSX.Element {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const entityId = getAccessibleOrInaccessibleId(props.data);

  const isMainEntityMapNode = EntityMapMachineContext.useSelector(
    (snap) => snap.context.entityId === entityId
  );

  // We use EntityMapMachineContext.useSelector here instead of the useEntityMapActorRef alias so that we're
  // able to effectively stub this in the unit tests
  const nodeActor = EntityMapMachineContext.useSelector((snap) =>
    snap.context.spawnedNodeMachines.get(entityId)
  );

  // isIdle determines if the node can load the extended graph
  const isIdle = useSelector(nodeActor, (snap) => snap?.matches('idle')) ?? false;
  const isLoading = useSelector(nodeActor, (snap) => snap?.matches('loading')) ?? false;
  const isError = useSelector(nodeActor, (snap) => snap?.matches('error')) ?? false;
  const isDone = useSelector(nodeActor, (snap) => snap?.matches('done')) ?? false;

  const status: BenchlingNodeV2Status | undefined = isLoading ? 'pending' : isError ? 'error' : undefined;

  const onClick = useCallback(() => {
    if (!isIdle) {
      return;
    }
    nodeActor!.send({
      type: 'loadExtendedGraph',
    });
  }, [nodeActor, isIdle]);

  return (
    <BenchlingNodeV2
      status={status}
      pb="0px"
      w="auto"
      maw="200px"
      sx={{
        ...(isMainEntityMapNode
          ? {
              border: `1px solid ${Color.BLUE_450}`,
            }
          : {}),
      }}
      onMouseEnter={(e) => {
        setIsHovered(true);
        e.stopPropagation();
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
      }}
      {...getTestProps({
        component: 'EntityNode',
        element: 'root',
      })}
    >
      <BenchlingNodeV2.Header
        spacing="xs"
        {...(isMainEntityMapNode
          ? {
              bg: isError ? Color.RED_100 : Color.BLUE_50,
            }
          : {})}
        {...getTestProps({
          component: 'EntityNode',
          element: 'header',
        })}
      >
        <EntityNodeHeader {...props.data} />
      </BenchlingNodeV2.Header>
      <EntityNodeHandle nodeId={props.id} handleType="target" />
      <EntityNodeHandle
        nodeId={props.id}
        handleType="source"
        onClick={onClick}
        isHovered={isHovered}
        setIsHovered={setIsHovered}
        isFullyExpanded={isDone}
        // TODO: update with more granular logic for when the node cannot be expanded
        cannotBeExpanded={isError}
        tooltipText={isError ? HOVER_INFO_TOOLTIP_TEXT.TOO_MANY_RELATIONSHIPS : undefined}
      />
    </BenchlingNodeV2>
  );
}

const ENTITY_NODE_HEADER_CHIP_STYLES = StyleSheet.create({
  chip: {
    marginRight: '0px',
  },
});
